import React from 'react';
import { Check } from 'lucide-react';
import { pipelineStepsFor, PIPELINE_STAGES } from '../../utils/projectPipeline.js';

/**
 * PROJECT PIPELINE stepper (guided journey) — a compact horizontal Plan → Review → Finalize → Run
 * band under the composer, with the CURRENT stage highlighted so the user always knows where they are
 * and what's next (PROJECT-NETWORKS.md §8). Presentational only; the active stage is derived upstream
 * (``deriveProjectStage``) from the ledger, so it stays in sync with the next-step proposal.
 *
 * @param {{stage:string, nextLabel?:string, testId?:string}} props
 *   stage — active stage key ('plan'|'review'|'finalize'|'run')
 *   nextLabel — the deterministic next-action text (e.g. "Approve Scoping"); shown as the caption.
 */
export default function ProjectPipeline({ stage, nextLabel = '', testId = 'project-pipeline' }) {
  const steps = pipelineStepsFor(stage);
  const active = steps.find((s) => s.state === 'active') || PIPELINE_STAGES[0];
  return (
    <div data-testid={testId} className="flex flex-col items-center gap-0.5">
      <ol className="flex items-center gap-1" aria-label="Project pipeline">
        {steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-1">
            <span
              data-testid={`pipeline-step-${s.key}`}
              data-state={s.state}
              title={s.hint}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                s.state === 'active'
                  ? 'bg-indigo-600 text-white'
                  : s.state === 'done'
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {s.state === 'done' ? <Check className="h-2.5 w-2.5" aria-hidden /> : null}
              {s.label}
            </span>
            {i < steps.length - 1 ? (
              <span className={`h-px w-3 ${s.state === 'done' ? 'bg-emerald-300' : 'bg-gray-200'}`} aria-hidden />
            ) : null}
          </li>
        ))}
      </ol>
      <div className="text-[11px] leading-snug text-gray-400">
        {nextLabel ? <>Next: <span className="text-gray-600">{nextLabel}</span></> : active.hint}
      </div>
    </div>
  );
}
